import React, { useState } from "react";
import { TableHead, TableCell, TableRow, Tooltip } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import MUICard from "../../../../Component/UI/MUICard/MUICard";

import classes from "./Taxonomy.module.css";

import MUIIconButton from "../../../../Component/UI/MUIIconButton/MUIIconButton";
import { extractDomain } from "../../../../utils/helpers";
import MUITable, {
  StyledTableCell,
  StyledTableRow,
} from "../../../../Component/UI/Table/Table";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import AIGenerateButton from "../../../../Component/AIGenerateButton/AIGenerateButton";
import Spinner from "../../../../Component/Spinner/Spinner";
import MUIToolTip from "../../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  formatName,
  sourceSplitToDomain,
} from "../CoreAttributes/CoreAttributes";
import { getSourcesWithAlias } from "../../../../services/apiService";
import { createDomainUrlMapping } from "../ActionComponent/ActionModal";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../../redux/actions/metricActions";
import { useDispatch } from "react-redux";
import { ENTITY_LABELS } from "../../../../utils/labelMapping";


function Row({
  category,
  selectedInput,
  sources,
  sourceURLIds,
  customerId,
  fetchRealTimeActionsSources,
}) {
  const [open, setOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const dispatch = useDispatch();

  function roundNumber(num) {
    return num % 1 < 0.5 ? Math.floor(num) : Math.ceil(num);
  }

  const saveActions = async (title, rationale, realSource) => {
    setIsActionLoading(true);
    realSource = sourceSplitToDomain(realSource);
    const llmSources =
      sources && Object.values(sources)?.[0]
        ? Object.keys(Object.values(sources)[0]).slice(0, 5)
        : [];
    let sourceFullUrls = [];
    if (llmSources.length > 0) {
      sourceFullUrls = await getSourcesWithAlias({
        sources: llmSources,
        sourceAliasIds: sourceURLIds,
      });
    }
    const domainUrlMapping = createDomainUrlMapping(sourceFullUrls?.data);
    const finalSource =
      realSource && realSource.length > 0
        ? realSource
        : llmSources.map((url) => ({
            domain_url: url,
            fullUrl: domainUrlMapping?.[url] || "",
          }));
    const modifiedObj = {
      title: title,
      rationale: rationale,
      brand: selectedInput?.brand || selectedInput?.product,
      persona: selectedInput?.persona,
      geography: selectedInput?.geography,
      market: selectedInput?.market,
      llm: selectedInput?.llm,
      isProduct: selectedInput?.product ? true : false,
      sources: finalSource,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };

    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);

        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  };

  return (
    <React.Fragment key={category.name}>
      <StyledTableRow>
        <StyledTableCell className={classes.attribute_headings}>
          <div className={classes.heading_wrapper}>
            <MUIIconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </MUIIconButton>
            <span className={classes.heading_text}>{category.name}</span>
          </div>
        </StyledTableCell>
        {category?.products?.map((product, index) => {
          let color = "#dc3545";
          if (product.score >= 7) color = "#2196F3";
          if (product.score >= 9) color = "#198754";

          const isCustomTooltip =
            product?.name === (selectedInput?.brand || selectedInput?.product);

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
                metric: ENTITY_LABELS.productDimension,
                realtimeSources: fetchRealTimeActionsSources,
                attribute: formatName(category.name),
                metricScore: Number(product.score).toFixed(0),
                selectedInput,
                sources,
                key: index,
                addToActionHandler: saveActions,
                actionText: "Add To Action",
                title: product?.explanation,
                isActionLoading: isActionLoading,
                isBrandOverview: true,
                arrow: true,
                slotProps: {
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -18],
                        },
                      },
                    ],
                  },
                },
              }
            : {};

          return (
            <TooltipComponent {...tooltipProps}>
              <StyledTableCell
                style={{ color }}
                className={classes.attribute_score}
                key={product.name}
              >
                {roundNumber(product.score)}
              </StyledTableCell>
            </TooltipComponent>
          );
        })}
      </StyledTableRow>
      {open &&
        category?.products?.[0].subcategories?.map((sub, idx) => (
          <StyledTableRow key={idx} className={classes.sub_row}>
            <StyledTableCell className={classes.attribute_sub_headings}>
              {sub.name}
            </StyledTableCell>
            {category?.products?.map((product, jdx) => {
              let color = "#dc3545";
              const item = product.subcategories[idx];
              if (item.score >= 7) {
                color = "#2196F3";
              }
              if (item.score >= 9) {
                color = "#198754";
              }

              return (
                <Tooltip
                  title={item.explanation}
                  arrow
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -18],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <StyledTableCell
                    style={{ color }}
                    className={classes.attribute_score}
                  >
                    {roundNumber(product.subcategories[idx].score)}
                  </StyledTableCell>
                </Tooltip>
              );
            })}
          </StyledTableRow>
        ))}
      <ToastContainer />
    </React.Fragment>
  );
}

export default function Taxonomy({
  categories,
  brandDomains,
  loading,
  errors,
  summaries,
  getAttributesSummary,
  selectedInput,
  sources,
  sourceURLIds,
  customerId,
  fetchRealTimeActionsSources,
}) {
  const TABLE_HEADERS = [
  {
    id: "taxonomy",
    name: ENTITY_LABELS.productDimension,
  },
];
  let competitors = categories?.map((obj) => {
    return obj?.products?.map((item) => item?.name);
  });
  competitors = Array.from(new Set(competitors?.flat()));
  function renderHeaders() {
    const headers = [
      ...TABLE_HEADERS,
      ...competitors?.map((item) => ({
        id: item,
        name: item,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers?.map((header, index) => {
            let domain = extractDomain(brandDomains?.[header?.name]);

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index != 0 && (
                    <img
                      data-html2canvas-ignore
                      className={classes.table_head_logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )}
                  <div
                    className={
                      index === 0
                        ? classes.table_head_text_comp
                        : classes.table_head_text
                    }
                  >
                    {header.name}
                  </div>
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function renderRows(category) {
    return (
      <Row
        customerId={customerId}
        selectedInput={selectedInput}
        sources={sources}
        sourceURLIds={sourceURLIds}
        key={category.name}
        category={category}
        fetchRealTimeActionsSources={fetchRealTimeActionsSources}
      />
    );
  }

  return (
    <>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        {categories?.length > 0 ? (
          <MUITable
            elevation={0}
            data={categories}
            renderHeaders={() => renderHeaders()}
            render={renderRows}
          />
        ) : (
          <>
            <h5>
              <Tooltip
              // title={metricExplainations[`Purchase Criteria`]}
              >
                {ENTITY_LABELS.productDimension}
              </Tooltip>
            </h5>
            <EmptyView message="Data Not Available" />
          </>
        )}
      </MUICard>

      <div className="col-md-12">
        <div className="graphGreyBg">
          <p className="mb-3 font14-600">
            Summary &nbsp; &nbsp;
            <AIGenerateButton
              fetchSummary={getAttributesSummary}
              type="product-dimensions"
            />
          </p>
          <div className="graphsummary font-11">
            {loading?.productDimensions ? (
              <Spinner />
            ) : summaries?.productDimensions ? (
              <p className="mb-1"> {summaries?.productDimensions} </p>
            ) : errors?.productDimensions ? (
              <p style={{ color: "red", textAlign: "center" }}>
                {errors?.productDimensions}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
